// auth.ts
import app from "./firebaseInit";
import { getAuth, GoogleAuthProvider, OAuthProvider, signInWithPopup, signInWithCredential, UserCredential, Auth } from "firebase/auth";

// Add this near the top with imports
declare global {
    interface Window {
        google?: any;
    }
}

export default class AuthService {
    // Lazy getter for auth
    private static get auth(): Auth | null {
        if (!app) return null;
        return getAuth(app);
    }

    private static googleSignIn = () => {
        const auth = this.auth;
        if (!auth) return;
        
        const provider = new GoogleAuthProvider();

        const returnUrl = (document.getElementById('ReturnUrl') as HTMLInputElement)?.value || '/';
        const referralCode = (document.getElementById('ReferralCode') as HTMLInputElement)?.value || '';

        signInWithPopup(this.auth, provider)
            .then(result => result.user.getIdToken())
            .then(idToken => this.sendTokenToServer(idToken, returnUrl, referralCode))
            .then(async res => {
                if (!res.ok) {
                    console.error(res);
                    alert('Server login failed');
                    return;
                }

                const data = await res.json();

                if (data.success) {
                    window.location.href = data.redirectUrl || returnUrl;

                    // Sending new users to a form to collect more data - leaving out for now
                    /*if (data.isNewUser) {
                        // 🚨 New user → go to extra signup step
                        window.location.href = `/Account/RegisterAdditionalData?customerId=${encodeURIComponent(data.customerId)}&isSocial=true`;
                    } else {
                        // Existing user → go straight to returnUrl
                        window.location.href = data.redirectUrl || returnUrl;
                    }*/
                } else {
                    alert('Server login failed');
                }
            })
            .catch(error => {
                console.error("Firebase login error:", error);
            });
    };

    private static appleSignIn = () => {
        if (!this.auth) return;

        const provider = new OAuthProvider('apple.com');
        provider.addScope('email');
        provider.addScope('name');

        const returnUrl = (document.getElementById('ReturnUrl') as HTMLInputElement)?.value || '/';
        const referralCode = (document.getElementById('ReferralCode') as HTMLInputElement)?.value || '';

        signInWithPopup(this.auth, provider)
            .then(result => result.user.getIdToken())
            .then(idToken => this.sendTokenToServer(idToken, returnUrl, referralCode))
            .then(async res => {
                if (!res.ok) {
                    alert('Login failed');
                    return;
                }

                const data = await res.json();

                if (data.success) {
                    window.location.href = data.redirectUrl || returnUrl;

                    // Sending new users to a form to collect more data - leaving out for now
                    /*if (data.isNewUser) {
                        // 🚨 New user → go to extra signup step
                        window.location.href = `/Account/RegisterAdditionalData?customerId=${encodeURIComponent(data.customerId)}&isSocial=true`;
                    } else {
                        // Existing user → go straight to returnUrl
                        window.location.href = data.redirectUrl || returnUrl;
                    }*/
                } else {
                    alert('Login failed');
                }
            })
            .catch(error => {
                console.error("Apple login error:", error);
            });
    };

    private static sendTokenToServer(idToken: string, returnUrl: string, referralCode: string ): Promise<Response> {
        const tokenInput = document.querySelector('[name="__RequestVerificationToken"]') as HTMLInputElement | null;
        const requestVerificationToken = tokenInput?.value ?? '';

        return fetch('/Account/FirebaseLogin', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'RequestVerificationToken': requestVerificationToken
            },
            body: JSON.stringify({ token: idToken, returnUrl, referralCode })
        });
    }

    // Public method to hook up buttons
    public static setupAuthButtons() {
        document.getElementById('google-signin-btn')?.addEventListener('click', this.googleSignIn);
        document.getElementById('apple-signin-btn')?.addEventListener('click', this.appleSignIn);

        // Initialize Google One Tap as well
        //this.setupGoogleOneTap();
    }

    // Public method to set up the little eye buttons on password fields
    public static setupPasswordToggles() {
        const toggleIcons = document.querySelectorAll<HTMLElement>('.password-toggle');

        toggleIcons.forEach(toggleIcon => {
            const inputId = toggleIcon.getAttribute('data-target');
            if (!inputId) return;

            const passwordInput = document.getElementById(inputId) as HTMLInputElement;
            if (!passwordInput) return;

            toggleIcon.addEventListener('click', () => {
                const icon = toggleIcon.querySelector('i');
                const isPassword = passwordInput.type === 'password';
                passwordInput.type = isPassword ? 'text' : 'password';
                icon?.classList.replace(isPassword ? 'fa-eye' : 'fa-eye-slash', isPassword ? 'fa-eye-slash' : 'fa-eye');
            });
        });
    }

    /**
     * Initialize Google One Tap
     */
    public static setupGoogleOneTap() {
        if (!this.auth) return;

        if (!window.google || window.isLoggedIn) {
            return;
        }

        const config = window.tenantAuthConfig;
        if (!config?.googleClientId) {
            return;
        }

        if (this.auth.currentUser) return;

        window.google.accounts.id.initialize({
            client_id: config.googleClientId,
            callback: this.handleOneTapCredential,
            auto_select: false,
            cancel_on_tap_outside: true
        });

        window.google.accounts.id.prompt();
    }

    /**
     * Handle One Tap response
     */
    private static handleOneTapCredential = (response: { credential: string }) => {
        if (!this.auth) return;

        const credential = response.credential;

        // Convert to Firebase credential
        const firebaseCredential = GoogleAuthProvider.credential(credential);

        signInWithCredential(this.auth, firebaseCredential)
            .then((userCredential: UserCredential) => userCredential.user.getIdToken())
            .then((idToken: string) => {
                const returnUrl = (document.getElementById('ReturnUrl') as HTMLInputElement)?.value || '/';
                const referralCode = (document.getElementById('ReferralCode') as HTMLInputElement)?.value || '';
                return this.sendTokenToServer(idToken, returnUrl, referralCode);
            })
            .then(async (res: Response) => {
                if (!res.ok) {
                    alert('Server login failed');
                    return;
                }

                const data = await res.json();
                if (data.success) {
                    window.location.href = data.redirectUrl || '/';
                } else {
                    alert('Server login failed');
                }
            })
            .catch((err: any) => console.error("One Tap Firebase login error:", err));
    }
}